// src\pages\Admin\Views\Marketing\MarketingLandingPage\MarketingLandingPageList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../../../services/apiServices'; // Assuming this is your configured axios instance //[cite: 6]
import { useToast } from '../../../../../hooks/use-toast'; ////[cite: 27]
// --- NEW IMPORTS ---
import { Button } from '../../../../../components/ui/button'; // Import Button if not already
import PaginationControls from '../../../../../components/Shared/PaginationControls'; // Import the shared component

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu'; // Import DropdownMenu components
import { Copy, Loader2 } from 'lucide-react'; // Import icons
// --- END NEW IMPORTS ---

const MarketingLandingPageList = () => {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    //const navigate = useNavigate();
    const { toast } = useToast();
    const countriesToCopy = ['en-in', 'fr-fr', 'en-lk']; // Add more as needed
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize] = useState(100); // Standard items per page


    const fetchPages = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const response = await api.get('/admin/marketing/landing-pages', {
                params: {
                    page: page,
                    limit: pageSize
                }
            });

            // The backend returns MLPPaginatedResponse: { items, total, page, limit }
            const { items, total } = response.data;
            
            setPages(items || []);
            setTotalItems(total || 0);
            setCurrentPage(page);
            
            console.log(`[MLP List] Successfully fetched page ${page}. Total items: ${total}`);
        } catch (error) {
            console.error("[MLP List] Fetch Error:", error);
            toast({
                variant: "destructive",
                title: "Fetch Failed",
                description: error.response?.data?.detail || "Could not retrieve landing pages. Please try again.",
            });
        } finally {
            setLoading(false);
        }
    }, [pageSize, toast]);

    useEffect(() => {
        fetchPages(1);
    }, [fetchPages]);

    const handlePageChange = (newPage) => {
        fetchPages(newPage);
        // Smooth scroll to top of table on page change
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCopy = async (pageId) => {
        if (!window.confirm("Are you sure you want to copy this page? A new page will be created with a unique slug.")) return;
        try {
            await api.post(`/admin/marketing/landing-pages/${pageId}/copy`);
            toast({ title: "Page Copied", description: "The landing page has been duplicated successfully." });
            fetchPages(); // Refresh the list
        } catch (error) {
            console.error("Failed to copy page:", error);
            toast({ variant: "destructive", title: "Error", description: "Could not copy the page." }); //[cite: 27]
        }
    };

    const handleDelete = async (pageId) => {
        if (!window.confirm("Are you sure you want to permanently delete this page?")) return;
        try {
            await api.delete(`/admin/marketing/landing-pages/${pageId}`);
            toast({ title: "Page Deleted", description: "The landing page has been removed." });
            setPages(pages.filter(p => p.id !== pageId)); // Optimistic UI update
        } catch (error) {
            console.error("Failed to delete page:", error);
            toast({ variant: "destructive", title: "Error", description: "Could not delete the page." }); //[cite: 27]
        }
    };

    const handleCopyToClipboard = async (countryCode, slug) => {
        // Construct the live URL (same as view link)
        const liveDomain = process.env.REACT_APP_STAGE === 'prod' ? 'www.selyst.com' : 'dev.selyst.com'; // Adjust domain as needed
        const urlToCopy = `https://${liveDomain}/${countryCode}/mlp/${slug}`;

        try {
            await navigator.clipboard.writeText(urlToCopy);
            toast({
                title: "Link Copied!",
                description: `Live URL for ${slug} (${countryCode}) copied to clipboard.`,
                variant: 'default', // Or 'success' if you have one
            });
        } catch (err) {
            console.error('Failed to copy URL:', err);
            toast({
                variant: "destructive",
                title: "Copy Failed",
                description: "Could not copy the URL to the clipboard.",
            });
        }
    };

    const tableCellStyle = { padding: '8px 12px', borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' };
    const linkStyle = { color: '#2B5DB9', textDecoration: 'underline', cursor: 'pointer' }; //[cite: 6]

    if (loading) return <div>Loading...</div>;

    return (
        <div className="p-4">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1 className="text-2xl font-bold">Marketing Landing Pages</h1>
                <Link to="/admin/marketing/buyer-landing-pages/new" style={{ ...linkStyle, textDecoration: 'none', padding: '8px 12px', border: '1px solid #2B5DB9', borderRadius: '4px' }}>
                    Create New Page
                </Link>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                        <tr>
                            <th style={{...tableCellStyle, textAlign: 'left'}}>Category</th>
                            <th style={{...tableCellStyle, textAlign: 'left'}}>Slug</th>
                            <th style={{...tableCellStyle, textAlign: 'center', width: '80px'}}>View</th>
                            <th style={{...tableCellStyle, textAlign: 'center', width: '80px'}}>Edit</th>
                            <th style={{...tableCellStyle, textAlign: 'center', width: '80px'}}>Copy</th>
                            <th style={{...tableCellStyle, textAlign: 'center', width: '100px'}}>Live Link</th>
                            <th style={{...tableCellStyle, textAlign: 'center', width: '80px'}}>Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="7" style={{...tableCellStyle, textAlign: 'center', padding: '40px'}}>
                                    <Loader2 className="animate-spin inline mr-2" /> Loading pages...
                                </td>
                            </tr>
                        ) : pages.length > 0 ? (
                            pages.map(page => (
                                <tr key={page.id} className="hover:bg-gray-50 transition-colors">
                                    <td style={tableCellStyle}>{page?.master_category_name || 'N/A'}</td>
                                    <td style={tableCellStyle} className="font-mono text-xs">/mlp/{page.slug}</td>
                                    {/* Action items same as before... */}
                                    <td style={{...tableCellStyle, textAlign: 'center'}}>
                                        <a href={`https://${process.env.REACT_APP_STAGE === 'prod' ? 'www' : 'dev'}.selyst.com/mlp/${page.slug}`} target="_blank" rel="noopener noreferrer" style={linkStyle}>View</a>
                                    </td>
                                    <td style={{...tableCellStyle, textAlign: 'center'}}>
                                        <Link to={`/admin/marketing/buyer-landing-pages/edit/${page.id}`} style={linkStyle}>Edit</Link>
                                    </td>
                                    <td style={{...tableCellStyle, textAlign: 'center'}}>
                                        <span onClick={() => handleCopy(page.id)} style={linkStyle}>Copy</span>
                                    </td>
                                    <td style={{...tableCellStyle, textAlign: 'center'}}>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-8 w-8"><Copy className="h-4 w-4" /></Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuLabel>Copy Live Link For:</DropdownMenuLabel>
                                                <DropdownMenuSeparator />
                                                {countriesToCopy.map((code) => (
                                                    <DropdownMenuItem key={code} onSelect={() => handleCopyToClipboard(code, page.slug)}>
                                                        {code.toUpperCase()}
                                                    </DropdownMenuItem>
                                                ))}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </td>
                                    <td style={{...tableCellStyle, textAlign: 'center'}}>
                                        <span onClick={() => handleDelete(page.id)} style={{...linkStyle, color: '#DC2626'}}>Delete</span>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" style={{...tableCellStyle, textAlign: 'center'}}>No landing pages found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* --- PAGINATION CONTROLS --- */}
            {!loading && totalItems > 0 && (
                <div className="mt-6 flex flex-col items-center gap-4">
                    <p className="text-sm text-gray-500">
                        Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
                    </p>
                    <PaginationControls
                        currentPage={currentPage}
                        totalPages={Math.ceil(totalItems / pageSize)}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}
        </div>
    );
};

export default MarketingLandingPageList;